import { PROMOTION_TYPE } from "@components/features/AutoPromotionPopup/constants";
import { dispatchAnalyticsEvent } from "@configs/analytics/dispatch";
import { EVENTS, EventName } from "@configs/analytics/events/constants";
import {
  hasSeenAndMarkCloseDeposit,
  hasSeenAndMarkDeposit,
  hasSeenAndMarkRegistrationFormFocus,
  isValidEvent,
} from "@configs/analytics/events/dedupe";
import { DepositFunnelTracker, RegistrationFunnelTracker } from "@configs/analytics/events/funnel";
import {
  AuthSource,
  CashbackMiniPromptShownMetadata,
  ClaimCashbackMiniPromptMetadata,
  ClickHomeBannerMetadata,
  ClickMiniBannerMetadata,
  ClickPromotionItemMetadata,
  ClickPromotionPageMetadata,
  CloseDepositIframeMetadata,
  CloseDepositNativeMetadata,
  DepositCreateResultOptions,
  DepositGroupsEmptyMetadata,
  DepositIframeCloseReason,
  DepositMethodsEmptyMetadata,
  DepositNativeCloseReason,
  DepositSource,
  DepositStep,
  EventMetadata,
  GameExitParams,
  GoogleAuthSource,
  LanguageChangeSource,
  NotificationAction,
  NotificationBrowserPromptResponseMetadata,
  NotificationInAppPromptResponseMetadata,
  NotificationInAppPromptShownMetadata,
  NotificationInAppSource,
  NotificationSubscriptionStatusMetadata,
  OTPFailedAttemptMetadata,
  OTPMethod,
  OTPSource,
  RegistrationFormFocusMetadata,
  RegistrationMethod,
  RegistrationStep,
} from "@configs/analytics/events/types";
import { identifyUser } from "@configs/analytics/identify";
import { NotificationPermissionState } from "@types";
import { getStoredCredential } from "@utils/credentials";
import logger from "@utils/logger";
import { getReferralCode } from "@utils/referral";
import {
  GameModel,
  LoginGrantType,
  MemberModel,
  MemberSettingAuthMode,
  WalletTxMethod,
  WithdrawRequest,
  WithdrawResponse,
} from "kz-ui-sdk";

// ===== Core dispatcher =====

export type TrackFn = (eventName: EventName | string, metadata?: EventMetadata, value?: number) => void;

/**
 * Shared dispatch core: validates event name (dev warning), then forwards to
 * the active provider via `dispatchAnalyticsEvent`. Used as the `track`
 * callback by both `useAnalyticsTracking` and `AnalyticsTracker`.
 */
export const dispatchTrackedEvent: TrackFn = (eventName, metadata, value) => {
  if (!isValidEvent(eventName) && !eventName.startsWith("act:") && !eventName.startsWith("evt:")) {
    logger._console.warn(`Event name "${eventName}" doesn't follow naming convention (act: or evt:)`);
  }
  dispatchAnalyticsEvent(eventName, metadata, value);
};

// ===== Registration =====

export function trackRegisterClick(track: TrackFn, source: AuthSource): void {
  const referralCode = getReferralCode();
  const tracker = RegistrationFunnelTracker.getInstance();
  tracker.startRegistration();
  tracker.addStep(RegistrationStep.CLICK_REGISTER);

  track(EVENTS.CLICK_REGISTER, {
    source,
    step: RegistrationStep.CLICK_REGISTER,
    referralCode,
    hasReferralCode: !!referralCode,
  });
}

export function trackRegisterFormFocus(track: TrackFn, method: RegistrationMethod): void {
  if (hasSeenAndMarkRegistrationFormFocus(method)) return;
  const metadata: RegistrationFormFocusMetadata = {
    method,
    pathname: window.location.pathname,
    fromPathname:
      (window.history?.state as { usr?: { from?: { pathname?: string } } } | null)?.usr?.from?.pathname ?? undefined,
    search: window.location.search ?? undefined,
  };
  track(EVENTS.REGISTRATION_FORM_FOCUS, metadata);
}

export function trackRegisterFormSubmit(
  track: TrackFn,
  method: RegistrationMethod,
  options?: { timeSpentOnForm?: number; retryCount?: number },
): void {
  const referralCode = getReferralCode();
  const tracker = RegistrationFunnelTracker.getInstance();
  tracker.addStep(RegistrationStep.FORM_SUBMIT);
  const retryCount = tracker.incrementAttempt("register_form_submit");

  track(EVENTS.REGISTER_FORM_SUBMIT, {
    method,
    step: RegistrationStep.FORM_SUBMIT,
    timeSpentOnForm: options?.timeSpentOnForm,
    retryCount: options?.retryCount || retryCount,
    referralCode,
    hasReferralCode: !!referralCode,
  });
}

export function trackOTPSent(
  track: TrackFn,
  method: string,
  options?: { deliveryTime?: number; retryCount?: number; source?: OTPSource },
): void {
  const tracker = RegistrationFunnelTracker.getInstance();
  const isRegisterSource = (options?.source ?? OTPSource.REGISTER) === OTPSource.REGISTER;
  if (isRegisterSource) {
    tracker.addStep(RegistrationStep.OTP_SENT);
  }
  const computedRetryCount = isRegisterSource ? tracker.incrementAttempt("otp_sent") : undefined;

  track(EVENTS.OTP_REQUEST_RESULT, {
    method,
    step: RegistrationStep.OTP_SENT,
    deliveryTime: options?.deliveryTime,
    retryCount: options?.retryCount || computedRetryCount,
    source: options?.source,
    success: true,
  });
}

export function trackOTPVerified(
  track: TrackFn,
  method: string,
  options?: { attemptsCount?: number; timeToVerify?: number; source?: OTPSource },
): void {
  const tracker = RegistrationFunnelTracker.getInstance();
  const isRegisterSource = (options?.source ?? OTPSource.REGISTER) === OTPSource.REGISTER;
  if (isRegisterSource) {
    tracker.addStep(RegistrationStep.OTP_VERIFY);
  }

  track(EVENTS.OTP_VERIFY_RESULT, {
    method,
    step: RegistrationStep.OTP_VERIFY,
    attemptsCount: options?.attemptsCount || 1,
    timeToVerify: options?.timeToVerify,
    source: options?.source,
    success: true,
  });
}

export function trackOTPRequestFailed(track: TrackFn, error: unknown, options?: { source?: OTPSource }): void {
  const otpTracker = RegistrationFunnelTracker.getInstance();
  const isRegisterSource = (options?.source ?? OTPSource.REGISTER) === OTPSource.REGISTER;

  const metadata: OTPFailedAttemptMetadata = {
    method: OTPMethod.SMS,
    step: RegistrationStep.OTP_SENT,
    error,
    attemptsCount: isRegisterSource ? otpTracker.incrementAttempt("otp_request") : 1,
    source: options?.source,
  };
  track(EVENTS.OTP_REQUEST_RESULT, { ...metadata, success: false });
}

export function trackOTPFailedAttempt(track: TrackFn, error: unknown, options?: { source?: OTPSource }): void {
  const failTracker = RegistrationFunnelTracker.getInstance();
  const isRegisterSource = (options?.source ?? OTPSource.REGISTER) === OTPSource.REGISTER;
  const metadata = {
    method: OTPMethod.SMS,
    step: RegistrationStep.OTP_VERIFY,
    attemptsCount: isRegisterSource ? failTracker.incrementAttempt("otp_verify_fail") : 1,
    error,
    source: options?.source,
    success: false,
  };

  track(EVENTS.OTP_VERIFY_RESULT, metadata);
}

export function trackOTPRequest(track: TrackFn, method: string, options?: { source?: OTPSource }): void {
  track(EVENTS.OTP_REQUEST, {
    method,
    source: options?.source,
  });
}

export async function trackRegistrationComplete(
  track: TrackFn,
  method: RegistrationMethod,
  options?: { member?: MemberModel; serverPrefix?: string },
): Promise<void> {
  try {
    const referralCode = getReferralCode();
    const tracker = RegistrationFunnelTracker.getInstance();
    tracker.addStep(RegistrationStep.REGISTRATION_COMPLETE);

    if (options?.member) {
      await identifyUser({ member: options.member, serverPrefix: options.serverPrefix });
    }

    track(EVENTS.REGISTRATION_RESULT, {
      method,
      step: RegistrationStep.REGISTRATION_COMPLETE,
      hasReferralCode: !!referralCode,
      referralCode,
      totalTimeToComplete: Math.floor(tracker.getTotalTime() / 1000),
      stepsCompleted: tracker.getCompletedSteps(),
      success: true,
    });
  } catch (error) {
    logger._console.error("[analytics] Failed to complete registration tracking:", error);
  }
}

export function trackRegistrationFailed(track: TrackFn, method: RegistrationMethod, error: unknown): void {
  const referralCode = getReferralCode();
  const tracker = RegistrationFunnelTracker.getInstance();

  track(EVENTS.REGISTRATION_RESULT, {
    method,
    hasReferralCode: !!referralCode,
    referralCode,
    success: false,
    error,
    totalTimeToComplete: Math.floor(tracker.getTotalTime() / 1000),
  });
}

// ===== Login / Auth =====

export function trackLoginClick(track: TrackFn, source: AuthSource): void {
  track(EVENTS.CLICK_LOGIN, { source });
}

export function trackGoogleAuthClick(track: TrackFn, source: GoogleAuthSource): void {
  track(EVENTS.CLICK_GOOGLE_AUTH, { source });
}

export function trackAuthClick(track: TrackFn, source: AuthSource, requireRegister: boolean = true): void {
  const hasSavedPhoneNumber = getStoredCredential()?.clientId;
  if (hasSavedPhoneNumber) {
    trackLoginClick(track, source);
  } else {
    if (requireRegister) {
      trackRegisterClick(track, source);
    } else {
      trackLoginClick(track, source);
    }
  }
}

export function trackLogin(
  track: TrackFn,
  authMode: MemberSettingAuthMode,
  options?: { grantType?: LoginGrantType },
): void {
  track(EVENTS.LOGIN, {
    authMode,
    grantType: options?.grantType?.toString(),
  });
}

export function trackLoginFormSubmit(
  track: TrackFn,
  authMode: MemberSettingAuthMode,
  grantType: LoginGrantType,
  options?: { timeSpentOnForm?: number },
): void {
  track(EVENTS.LOGIN_FORM_SUBMIT, {
    authMode,
    grantType: grantType?.toString(),
    timeSpentOnForm: options?.timeSpentOnForm,
  });
}

export function trackLoginFailed(
  track: TrackFn,
  error: string | undefined,
  options?: { authMode?: MemberSettingAuthMode; grantType?: LoginGrantType },
): void {
  track(EVENTS.LOGIN_FAILED, {
    authMode: options?.authMode,
    grantType: options?.grantType?.toString(),
    error,
  });
}

// ===== Deposit =====

export function trackDepositClick(
  track: TrackFn,
  source: DepositSource | null | undefined,
  options?: { accountBalance?: number },
): void {
  const tracker = DepositFunnelTracker.getInstance();
  tracker.startDeposit();
  tracker.addStep(DepositStep.CLICK_DEPOSIT);

  track(EVENTS.CLICK_DEPOSIT, {
    source: source ?? null,
    previousPage: window.location.pathname,
    accountBalance: options?.accountBalance,
  });
}

export function trackDepositMethodGroup(
  track: TrackFn,
  groupId: string,
  groupName: string,
  source?: DepositSource | null,
): void {
  const tracker = DepositFunnelTracker.getInstance();
  tracker.addStep(DepositStep.SELECT_GROUP);
  tracker.setSelectedData("groupId", groupId);
  tracker.setSelectedData("groupName", groupName);

  track(EVENTS.SELECT_DEPOSIT_METHOD_GROUP, {
    source: source ?? null,
    step: DepositStep.SELECT_GROUP,
    groupId,
    groupName,
  });
}

export function trackDepositMethodIndividual(
  track: TrackFn,
  methodId: string,
  methodName: string,
  options?: {
    source?: DepositSource;
    methodKey?: string;
    groupId?: string;
    isRecommended?: boolean;
    fees?: number;
    processingTime?: string;
  },
): void {
  const tracker = DepositFunnelTracker.getInstance();
  tracker.addStep(DepositStep.SELECT_METHOD);
  tracker.setSelectedData("methodId", methodId);
  tracker.setSelectedData("methodName", methodName);

  track(EVENTS.SELECT_DEPOSIT_METHOD_INDIVIDUAL, {
    source: options?.source,
    step: DepositStep.SELECT_METHOD,
    methodId,
    methodName,
    methodKey: options?.methodKey,
    groupId: options?.groupId,
    isRecommended: options?.isRecommended,
    fees: options?.fees,
    processingTime: options?.processingTime,
  });
}

export function trackAutoFastTriggered(
  track: TrackFn,
  autoSelectedMethod: string,
  options?: {
    source?: DepositSource;
    originalMethod?: string;
    reason?: string;
    timeSaved?: string;
  },
): void {
  track(EVENTS.AUTO_FAST_TRIGGERED, {
    source: options?.source,
    step: DepositStep.SELECT_METHOD,
    originalMethod: options?.originalMethod,
    autoSelectedMethod,
    reason: options?.reason || "auto_selection",
    timeSaved: options?.timeSaved,
  });
}

export function trackDepositAmountSelect(
  track: TrackFn,
  amount: number,
  options?: {
    source?: DepositSource;
    selectedMethod?: string;
    isQuickAmount?: boolean;
    bonusOffered?: number;
    currency?: string;
  },
): void {
  const tracker = DepositFunnelTracker.getInstance();
  tracker.addStep(DepositStep.SELECT_AMOUNT);
  tracker.setSelectedData("amount", amount);

  track(EVENTS.SELECT_DEPOSIT_AMOUNT, {
    source: options?.source,
    step: DepositStep.SELECT_AMOUNT,
    amount,
    selectedMethod: options?.selectedMethod,
    bonusOffered: options?.bonusOffered,
    currency: options?.currency,
  });
}

export function trackTopUpFailed(track: TrackFn, error: unknown, options?: DepositCreateResultOptions): void {
  track(EVENTS.DEPOSIT_CREATE_RESULT, {
    source: options?.source,
    currency: options?.currency,
    error,
    success: false,
    methodId: options?.methodId,
    methodName: options?.methodName,
    orderRef: options?.orderRef,
    depositGroupId: options?.depositGroupId,
    depositGroupName: options?.depositGroupName,
    methodKey: options?.methodKey,
    amount: options?.amount,
    providerKey: options?.providerKey,
    paymentMethod: options?.paymentMethod,
    payDataType: options?.payDataType,
  });
}

export function trackTopUpSubmit(
  track: TrackFn,
  amount: number,
  methodId: string,
  methodName: string,
  options?: {
    source?: DepositSource;
    currency?: string;
    estimatedProcessingTime?: string;
  },
): void {
  const tracker = DepositFunnelTracker.getInstance();
  tracker.addStep(DepositStep.SUBMIT_TOP_UP);

  track(EVENTS.TOP_UP_SUBMIT, {
    source: options?.source,
    step: DepositStep.SUBMIT_TOP_UP,
    amount,
    methodId,
    methodName,
    currency: options?.currency,
    estimatedProcessingTime: options?.estimatedProcessingTime,
  });
}

export function trackDepositSuccess(
  track: TrackFn,
  amount: number,
  transactionId: string,
  options?: {
    source?: DepositSource;
    method?: string;
    currency?: string;
    actualProcessingTime?: number;
    methodId?: string;
    providerKey?: string;
    isFirstDeposit?: boolean;
  },
): void {
  if (hasSeenAndMarkDeposit(transactionId)) {
    logger._console.log("[analytics] Skipping duplicate deposit_success for", transactionId);
    return;
  }

  const tracker = DepositFunnelTracker.getInstance();
  tracker.addStep(DepositStep.DEPOSIT_COMPLETE);

  track(EVENTS.DEPOSIT_SUCCESS, {
    source: options?.source,
    step: DepositStep.DEPOSIT_COMPLETE,
    amount,
    methodId: options?.methodId,
    method: options?.method,
    orderRef: transactionId,
    providerKey: options?.providerKey,
    currency: options?.currency,
    actualProcessingTime: options?.actualProcessingTime,
    totalDepositTime: Math.floor(tracker.getTotalTime() / 1000),
    isFirstDeposit: options?.isFirstDeposit,
  });
}

export function trackDepositFailed(
  track: TrackFn,
  amount: number,
  methodId: string,
  error: unknown,
  options?: {
    source?: DepositSource;
    methodName?: string;
    currency?: string;
    retryAttempt?: number;
    failurePoint?: string;
  },
): void {
  track(EVENTS.DEPOSIT_FAILED, {
    source: options?.source,
    step: DepositStep.SUBMIT_DEPOSIT,
    amount,
    methodId,
    methodName: options?.methodName,
    currency: options?.currency,
    error,
    retryAttempt: options?.retryAttempt || 1,
    failurePoint: options?.failurePoint,
  });
}

export function trackCloseDepositIframe(track: TrackFn, metadata: CloseDepositIframeMetadata): void {
  if (
    metadata.reason === DepositIframeCloseReason.DEPOSIT_SUCCESS &&
    hasSeenAndMarkCloseDeposit(metadata.orderRef, metadata.reason)
  ) {
    return;
  }
  track(EVENTS.CLOSE_DEPOSIT_IFRAME, {
    reason: metadata.reason,
    orderRef: metadata.orderRef,
  });
}

export function trackOpenDepositIframe(track: TrackFn, metadata: DepositCreateResultOptions): void {
  track(EVENTS.OPEN_DEPOSIT_IFRAME, {
    orderRef: metadata.orderRef,
    methodId: metadata.methodId,
    methodName: metadata.methodName,
    methodKey: metadata.methodKey,
    providerKey: metadata.providerKey,
    depositGroupId: metadata.depositGroupId,
    depositGroupName: metadata.depositGroupName,
    paymentMethod: metadata.paymentMethod,
    amount: metadata.amount,
    payDataType: metadata.payDataType,
  });
}

export function trackDepositCreateSuccess(track: TrackFn, amount: number, options: DepositCreateResultOptions): void {
  track(EVENTS.DEPOSIT_CREATE_RESULT, {
    source: options?.source,
    currency: options?.currency,
    success: true,
    amount,
    methodId: options?.methodId,
    methodName: options?.methodName,
    orderRef: options?.orderRef,
    depositGroupId: options?.depositGroupId,
    depositGroupName: options?.depositGroupName,
    methodKey: options?.methodKey,
    providerKey: options?.providerKey,
    paymentMethod: options?.paymentMethod,
    payDataType: options?.payDataType,
  });
}

export function trackCloseDepositCustom(track: TrackFn, metadata: CloseDepositNativeMetadata): void {
  if (
    metadata.reason === DepositNativeCloseReason.DEPOSIT_SUCCESS &&
    hasSeenAndMarkCloseDeposit(metadata.orderRef, metadata.reason)
  ) {
    return;
  }
  track(EVENTS.CLOSE_DEPOSIT_CUSTOM, {
    orderRef: metadata.orderRef,
    reason: metadata.reason,
  });
}

export function trackOpenDepositCustom(track: TrackFn, metadata: DepositCreateResultOptions): void {
  track(EVENTS.OPEN_DEPOSIT_CUSTOM, {
    orderRef: metadata.orderRef,
    methodId: metadata.methodId,
    methodName: metadata.methodName,
    methodKey: metadata.methodKey,
    providerKey: metadata.providerKey,
    depositGroupId: metadata.depositGroupId,
    depositGroupName: metadata.depositGroupName,
    paymentMethod: metadata.paymentMethod,
    amount: metadata.amount,
    payDataType: metadata.payDataType,
  });
}

export function trackDepositGroupsEmpty(track: TrackFn, metadata: DepositGroupsEmptyMetadata): void {
  track(EVENTS.DEPOSIT_GROUPS_EMPTY, metadata);
}

export function trackDepositMethodsEmpty(track: TrackFn, metadata: DepositMethodsEmptyMetadata): void {
  track(EVENTS.DEPOSIT_METHODS_EMPTY, metadata);
}

// ===== Gameplay =====

function gameMetadata(game: Partial<GameModel>) {
  return {
    gameId: game?.id,
    gameName: game?.name,
    gameProvider: game?.providerRef,
    gameProviderId: game?.gameProviderId,
    gameType: game?.type,
    gameCategory: game?.category,
    gameDescription: game?.description,
    gameRank: game?.rank,
    gameStatus: game?.status,
    gamePrimaryImageUrl: game?.primaryImage?.url,
    isMaintenance: game?.isMaintenance,
  };
}

export function trackGameOpened(
  track: TrackFn,
  game: Partial<GameModel>,
  options?: { source?: string; searchTerm?: string },
): void {
  track(EVENTS.GAME_OPENED, {
    ...gameMetadata(game),
    source: options?.source,
    searchTerm: options?.searchTerm,
  });
}

export function trackGameOpenTriggered(
  track: TrackFn,
  game: Partial<GameModel>,
  options?: { source?: string; searchTerm?: string },
): void {
  track(EVENTS.GAME_OPEN_TRIGGERED, {
    ...gameMetadata(game),
    source: options?.source,
    searchTerm: options?.searchTerm,
  });
}

export function trackGameSearch(
  track: TrackFn,
  searchTerm: string | undefined,
  options?: { resultCount?: number },
): void {
  track(EVENTS.GAME_SEARCH, {
    searchTerm,
    resultCount: options?.resultCount,
  });
}

export function trackGameSearchSelect(
  track: TrackFn,
  game: Partial<GameModel>,
  options?: { source?: string; searchTerm?: string },
): void {
  track(EVENTS.GAME_SEARCH_SELECT, {
    game,
    source: options?.source,
    searchTerm: options?.searchTerm,
  });
}

export function trackGameClosed(track: TrackFn, game: Partial<GameModel>): void {
  track(EVENTS.GAME_CLOSED, gameMetadata(game));
}

export function trackGameExit(track: TrackFn, gameInfo: GameExitParams): void {
  track(EVENTS.GAME_EXIT, {
    gameId: gameInfo.id,
    gameName: gameInfo.name,
    gameProviderSourceRef: gameInfo.providerSourceRef,
    gameProviderCode: gameInfo.providerGameCode,
    balanceChanged: gameInfo.balanceChanged,
    balanceDelta: gameInfo.balanceDelta,
  });
}

export function trackGameError(track: TrackFn, error: unknown, gameProviderId?: string): void {
  track(EVENTS.GAME_ERROR, {
    error,
    gameProviderId,
  });
}

export function trackGameContentVisibilityFallbackClick(track: TrackFn): void {
  track(EVENTS.GAME_CONTENT_VISIBILITY_FALLBACK_CLICK, {});
}

export function trackSessionTimeout(track: TrackFn): void {
  track(EVENTS.SESSION_TIMEOUT, {});
}

// ===== Promotions =====

export function trackPromoClaimAttempt(track: TrackFn, promoType: WalletTxMethod | "refer-ball"): void {
  track(EVENTS.PROMO_CLAIM_ATTEMPT, { promoType });
}

export function trackPromoClaimSuccess(track: TrackFn, promoType: WalletTxMethod | "refer-ball"): void {
  track(EVENTS.PROMO_CLAIM_SUCCESS, { promoType });
}

export function trackPromoIssued(track: TrackFn, promoType: WalletTxMethod): void {
  track(EVENTS.PROMO_ISSUED, { promoType });
}

export function trackPromoRewardApplied(track: TrackFn, promoId: string): void {
  track(EVENTS.PROMO_REWARD_APPLIED, { promoId });
}

export function trackPromoBonusExpired(track: TrackFn, promoId: string): void {
  track(EVENTS.PROMO_BONUS_EXPIRED, { promoId });
}

export function trackClickMiniBanner(track: TrackFn, metadata: ClickMiniBannerMetadata): void {
  track(EVENTS.CLICK_MINI_BANNER, metadata);
}

export function trackClickHomeBanner(track: TrackFn, metadata: ClickHomeBannerMetadata): void {
  track(EVENTS.CLICK_HOME_BANNER, metadata);
}

export function trackClickPromotionPage(track: TrackFn, metadata: ClickPromotionPageMetadata): void {
  track(EVENTS.CLICK_PROMOTION_PAGE, metadata);
}

export function trackClickPromotionItem(track: TrackFn, metadata: ClickPromotionItemMetadata): void {
  track(EVENTS.CLICK_PROMOTION_ITEM, metadata);
}

export function trackCashbackMiniPromptShown(track: TrackFn, metadata: CashbackMiniPromptShownMetadata): void {
  track(EVENTS.CASHBACK_MINI_PROMPT_SHOWN, metadata);
}

export function trackClaimCashbackMiniPrompt(track: TrackFn, metadata: ClaimCashbackMiniPromptMetadata): void {
  track(EVENTS.CLAIM_CASHBACK_MINI_PROMPT, metadata);
}

type AutoPromotionPopupType = (typeof PROMOTION_TYPE)[keyof typeof PROMOTION_TYPE];

export type AutoClaimPopupPromoType = "daily-bonus" | "daily-streak" | AutoPromotionPopupType;

export interface PromoAutoClaimPopupMetadata {
  promoType: AutoClaimPopupPromoType;
  streakCount?: number;
}

export function trackPromoAutoClaimPopup(track: TrackFn, metadata: PromoAutoClaimPopupMetadata): void {
  track(EVENTS.PROMO_AUTO_CLAIM_POPUP, metadata);
}

// ===== Withdrawals =====

export function trackWithdrawalClick(track: TrackFn): void {
  track(EVENTS.CLICK_WITHDRAWAL, {});
}

export function trackWithdrawalSubmit(track: TrackFn, payload: WithdrawRequest): void {
  track(EVENTS.WITHDRAWAL_SUBMIT, { payload });
}

export function trackWithdrawalSuccess(track: TrackFn, response: WithdrawResponse): void {
  track(EVENTS.WITHDRAWAL_SUCCESS, { response });
}

export function trackWithdrawalFailed(track: TrackFn, error: unknown): void {
  track(EVENTS.WITHDRAWAL_FAILED, { error });
}

// ===== Notifications =====

export function trackNotificationInAppPromptShown(
  track: TrackFn,
  source: NotificationInAppSource = NotificationInAppSource.PRIZES_PAGE,
): void {
  const metadata: NotificationInAppPromptShownMetadata = { source };
  track(EVENTS.NOTIFICATION_INAPP_PROMPT_SHOWN, metadata);
}

export function trackNotificationInAppPromptResponse(track: TrackFn, action: NotificationAction): void {
  const metadata: NotificationInAppPromptResponseMetadata = { action };
  track(EVENTS.NOTIFICATION_INAPP_PROMPT_RESPONSE, metadata);
}

export function trackNotificationBrowserPromptShown(track: TrackFn): void {
  track(EVENTS.NOTIFICATION_BROWSER_PROMPT_SHOWN, {});
}

export function trackNotificationBrowserPromptResponse(track: TrackFn, action: NotificationAction): void {
  const metadata: NotificationBrowserPromptResponseMetadata = { action };
  track(EVENTS.NOTIFICATION_BROWSER_PROMPT_RESPONSE, metadata);
}

export function trackNotificationSubscriptionStatus(track: TrackFn, status: NotificationPermissionState): void {
  const metadata: NotificationSubscriptionStatusMetadata = { status };
  track(EVENTS.NOTIFICATION_SUBSCRIPTION_STATUS, metadata);
}

// ===== Language =====

export function trackChangeLanguage(track: TrackFn, from: string, to: string, source: LanguageChangeSource): void {
  if (from === to) return;
  track(EVENTS.CHANGE_LANGUAGE, { from, to, source });
}
